import React, {useContext, useEffect, useState} from "react";
import "./GeneralSection.scss";
import {InvoiceItemsTable} from "../../components/InvoiceItemsTable/InvoiceItemsTable";
import {Invoice} from "../../models/invoice.model";
import {InvoiceContext} from "../../context/InvoiceContext";
import {ItemsContext} from "../../context/ItemsContext";
import {InvoiceOption, ItemCategory, ItemSection} from "../../models/item.model";
import {PreviewDataContext} from "../../context/PreviewContext";
import {PaymentModal} from "../../components/PaymentModal/PaymentModal";
import {WisetackInvoice} from "../../components/Wisetack/Wisetack";
import {FileContext} from "../../context/FileContext";
import {File} from "../../models/file.model";
import {AudioPlayer, SanitizedHtml, UI_PARAMS} from "@flicent/fieldpulse-react-component-library-frontend";
import {AddOnItemsContext} from "../../context/AddOnItemsContext";
import {InvoiceOptionTabs} from "../../components/InvoiceOptionTabs/InvoiceOptionTabs";
import {InvoiceOptionTile} from "../../components/InvoiceOption/InvoiceOptionTile";
import {OptionContext} from "../../context/OptionContext";
import {CompanyContext} from "../../context/CompanyContext";
import {Company} from "../../models/company.model";
import {InvoiceAddOnOptionTile} from "../../components/InvoiceAddOnOption/InvoiceAddOnOptionTile";
import {TruncateText} from "../../components/ui-components/TruncateText/TruncateText";
import AcornWidget from '../../components/AcornWidget/AcornWidget';
import {AcornContext} from '../../context/AcornContext';
import {SectionPreloader} from "../../components/SectionPreloader/SectionPreloader";
import {InvoiceSummaryRows} from "../../components/InvoiceSummary/InvoiceSummaryRows";
import {InvoiceActionRow} from "../../components/InvoiceActionRow/InvoiceActionRow";
import {usePayNow} from "../../hooks/usePayNow";

const TruncateTextLinkStyles: React.CSSProperties = {
    color: UI_PARAMS.FP_BRANDING_COLOR,
    fontWeight: 'bold'
};

const TruncateTextMaxLength = 80;

export const GeneralSection: React.FunctionComponent = () => {
    const isAcornEnabled: boolean = useContext(AcornContext);
    const invoice: Invoice = useContext(InvoiceContext);
    const company: Company = useContext(CompanyContext);
    const audioFile: File | null = useContext(FileContext).audio_file;
    const audioFileText: string | null = useContext(FileContext).audio_file_text;
    const optionContext = useContext(OptionContext);
    const itemSection: ItemSection = useContext(ItemsContext);
    const addOnItemSection = useContext(AddOnItemsContext);
    const previewContext = useContext(PreviewDataContext);
    const {onClickPayNow, isPaymentModalVisible, closePaymentModal} = usePayNow();
    const [currentCategories, setCurrentCategories] = useState<ItemCategory[]>([]);
    const [options, setOptions] = useState<InvoiceOption[]>([]);
    const [previewOption, setPreviewOption] = useState<InvoiceOption | null>(null);
    const [addOnOptions, setAddOnOptions] = useState<InvoiceOption[]>([]);
    const sanitizeHtmlOptions = {
        allowedAttributes: {
            '*': ['style', 'href', 'target'],
        },
        parseStyleAttributes: false,
    };

    useEffect(() => {
        setOptions(itemSection.options);
        if (itemSection && itemSection.options && (!previewOption || itemSection.options.indexOf(previewOption) === -1)) {
            setPreviewOption(itemSection.options[0]);
            setCurrentCategories(itemSection.options[0].categories);
            if (itemSection.options.length === 1) {
                onSelectOption(itemSection.options[0])
            }
        }
        if (itemSection && itemSection.options && previewOption && previewContext.isPreviewMode) {
            const newPreviewOption = itemSection.options.find((option) => {
                return option.id === previewOption.id;
            });
            if (newPreviewOption) {
                setPreviewOption(newPreviewOption);
                setCurrentCategories(newPreviewOption.categories);
            }
        }
    }, [itemSection, optionContext, previewOption]);

    useEffect(() => {
        const options = addOnItemSection.addOnItemSection?.options;
        setAddOnOptions(options as InvoiceOption[]);
    }, [addOnItemSection]);

    const isShowingFootnote = () => {
        return invoice.is_footnote_visible && invoice.footnote && invoice.footnote.length > 0;
    };

    const isShowingDisclaimer = () => {
        return company.is_franchise_invoice_disclaimer_visible && company.franchise_invoice_disclaimer && company.franchise_invoice_disclaimer.length > 0;
    };

    const onSelectOption = (selectedOption: InvoiceOption) => {
        if(selectedOption?.is_selected) {
            return;
        }
        const _options = itemSection.options.map(option => {
            return {...option, is_selected: selectedOption.id === option.id};
        });
        const newPreviewOption = _options.find((option) => {
            return option.id === selectedOption.id;
        })
        if (newPreviewOption) {
            setPreviewOption(newPreviewOption);
            onSelectOptionTab(newPreviewOption);
        }
        setOptions(_options);
        itemSection.options = _options;
        optionContext.selectOption(selectedOption);
    };

    const onSelectAddOnOption = (selectedOption: InvoiceOption) => {
        selectedOption.is_selected = !selectedOption.is_selected;
        setAddOnOptions([...addOnOptions]);
        optionContext.toggleAddOnOption(selectedOption);
    };

    const onSelectOptionTab = (selectedOption: InvoiceOption) => {
        setPreviewOption(selectedOption);
        setCurrentCategories(selectedOption.categories);
    };

    const getOptionTitle = (option: InvoiceOption | null) => {
        if (!option) {
            return ``;
        }

        return option.title || option.tab_title || option.option_name;
    };

    const loanAmount: number = optionContext.getAcornLoanAmount()

    return (
        <section className="general-section">
            <div className="summary-section">
                <section className="summary-section-content">
                    <div className="invoice-summary">
                        <div
                            className={`invoice-summary-loader ${optionContext.isCardFeeRecoverySummaryFetching ? '' : 'hidden'}`}>
                            <SectionPreloader/>
                        </div>

                        <h3>{invoice.invoice_name} Summary</h3>
                        <InvoiceSummaryRows/>

                        <InvoiceActionRow onClickPayNow={onClickPayNow}/>

                        <div className="wisetack-buttons">
                            <WisetackInvoice/>
                        </div>

                        {isAcornEnabled && loanAmount > 0 &&
                            <AcornWidget isPreviewMode={previewContext.isPreviewMode} loanAmount={loanAmount}/>}
                    </div>

                    {audioFile && <div className="audio-player-section">
                        <p className="audio-comment">
                            {audioFileText}
                        </p>
                        <AudioPlayer audioSource={audioFile.url} borderColor='#ebab78'/>
                    </div>}
                    {optionContext.selectedAddOnOptions.length > 0 &&
                        <div className='selections-totals'>
                            <h3>Selections</h3>

                            <div className="selection-row">
                                <p className='option-name'>
                                    <TruncateText linkStyles={TruncateTextLinkStyles}
                                                  maxLength={TruncateTextMaxLength}
                                                  text={getOptionTitle(optionContext.selectedOption)}/>
                                </p>
                                <p className='option-total'>
                                    {optionContext.selectedOption?.subtotal}
                                </p>
                            </div>

                            {
                                optionContext.selectedAddOnOptions.map((selectedAddOnOption: InvoiceOption) => {
                                    return (
                                        <div key={selectedAddOnOption.title} className="selection-row">
                                            <p className='option-name'>
                                                <TruncateText linkStyles={TruncateTextLinkStyles}
                                                              maxLength={TruncateTextMaxLength}
                                                              text={getOptionTitle(selectedAddOnOption)}/>
                                            </p>
                                            <p className='option-total'>
                                                {selectedAddOnOption.subtotal}
                                            </p>
                                        </div>
                                    )
                                })
                            }
                        </div>
                    }
                </section>
            </div>

            <div className="breakdown-section">
                <article className="breakdown-item">
                    {(options?.length > 1) && <InvoiceOptionTabs options={options} activeOption={previewOption}
                                                                 onSelectTab={onSelectOptionTab}
                                                                 onSelectOption={onSelectOption}/>}

                    {
                        previewOption &&
                        <InvoiceOptionTile option={previewOption}
                                           isSelectedOption={previewOption.is_selected}
                                           onSelectOption={onSelectOption}/>
                    }

                    {itemSection.is_items_visible && <InvoiceItemsTable invoice={invoice} itemSection={itemSection}
                                                                        categories={currentCategories}/>}
                </article>

                {
                    addOnOptions?.length > 0 && <>
                        <article className="breakdown-item">
                            <h3>Add-On Options</h3>

                            {
                                addOnOptions.map((option) => {
                                    return <section key={option.title} className='breakdown-item-section'>
                                        <InvoiceAddOnOptionTile option={option}
                                                                isSelectedOption={option.is_selected}
                                                                onSelectOption={onSelectAddOnOption}/>

                                        {addOnItemSection.addOnItemSection.is_items_visible &&
                                            <InvoiceItemsTable invoice={invoice}
                                                               itemSection={addOnItemSection.addOnItemSection}
                                                               categories={option.categories}/>}
                                    </section>
                                })
                            }

                        </article>
                    </>
                }

                <article className="breakdown-item">
                    {
                        isShowingFootnote()
                            ? <div className="invoice-footnotes">
                                <p><SanitizedHtml htmlContent={invoice.footnote} sanitizeOptions={sanitizeHtmlOptions}/></p>
                            </div>
                            : null
                    }
                </article>

                <article className="breakdown-item">
                    {
                        isShowingDisclaimer()
                            ? <div className="invoice-disclaimer">
                                <p>{company.franchise_invoice_disclaimer}</p>
                            </div>
                            : null
                    }
                </article>
            </div>

            {isPaymentModalVisible && <PaymentModal onCloseModal={closePaymentModal}/>}
        </section>
    );
};
